<template>
  <ValidationObserver
    ref="dialogObserver"
    v-slot="{ valid }"
    slim="slim"
  >
    <AppDialog
      class="overtime-policy-dialog"
      :title="title"
      append-to-body="append-to-body"
      show="show"
      @close="closeDialog"
    >
      <div
        v-if="fieldError('base')"
        class="form-group has-error"
      >
        <span class="help-block">{{ fieldError('base') }}</span>
      </div>

      <form
        id="overtime-policy-form"
        autocomplete="off"
        @submit.prevent="onFormSubmit"
      >
        <div
          v-if="editing && !disabled"
          class="form-group policy-alert edit-alert"
        >
          <i class="hi hi-info-filled mr-10" />
          <span> Changes to the policy will take effect at the start of the organization's next week. </span>
        </div>

        <ValidationProvider
          ref="nameProvider"
          rules="required"
          :name="form.name"
        >
          <div
            id="name"
            class="form-group mb-10"
            :class="{ 'has-error': fieldError('name') }"
          >
            <label
              class="control-label"
              for="name"
            >
              Policy Name
              <abbr
                v-if="!disabled"
                title="required"
              >
                *
              </abbr>
            </label>
            <AppInput
              v-model="form.name"
              class="form-control"
              placeholder="Policy name"
              required="required"
            />
            <span
              v-if="fieldError('name')"
              class="help-block"
            >
              {{ fieldError('name') }}
            </span>
          </div>
        </ValidationProvider>

        <!--
        @TODO: For V1, users are not able to be automatically added to overtime policies.
        <div
          id="auto_add_new_users"
          class="form-group mb-10"
          :class="{ 'has-error': fieldError('auto_add_new_users') }"
        >
          <AppCheckbox
            v-model="form.auto_add_new_users"
            class="mt-20"
          >
            Automatically add new members to this policy
          </AppCheckbox>
          <span
            v-if="fieldError('auto_add_new_users')"
            class="help-block"
          >
            {{ fieldError('auto_add_new_users') }}
          </span>
        </div>
        -->

        <div class="modal-subheading">Weekly overtime</div>

        <ValidationProvider
          ref="thresholdProvider"
          rules="required"
          :threshold="form.threshold"
        >
          <div
            id="threshold"
            class="form-group mb-10"
            :class="{ 'has-error': fieldError('threshold') }"
          >
            <label
              class="control-label"
              for="threshold"
            >
              Weekly overtime threshold
              <AppTooltipIcon
                class="ml-5"
                text-type="lighter"
                type="info-filled"
                message="Overtime will begin after working this many hours per week in organization's time zone"
                placement="top"
                :size="14"
              />
            </label>

            <div class="input-group">
              <AppInput
                v-model="form.threshold"
                :disabled="disabled"
                class="form-control"
                placeholder="40"
              />
              <span class="input-group-addon">hours</span>
            </div>
            <span
              v-if="fieldError('threshold')"
              class="help-block"
            >
              {{ fieldError('threshold') }}
            </span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          ref="pay_rate_multiplierProvider"
          rules="required"
          :pay-rate-multiplier="form.pay_rate_multiplier"
        >
          <div
            id="pay_rate_multiplier"
            class="form-group mb-10"
            :class="{ 'has-error': fieldError('pay_rate_multiplier') }"
          >
            <label
              class="control-label"
              for="pay_rate_multiplier"
            >
              Weekly overtime pay rate multiplier
            </label>

            <div class="multiplier">
              <AppInput
                v-model="form.pay_rate_multiplier"
                :disabled="disabled"
                class="form-control"
                placeholder="1.5"
              />
              <span class="mr-10">x</span>
              member's pay rate
            </div>

            <span
              v-show="fieldError('pay_rate_multiplier')"
              class="help-block"
            >
              {{ fieldError('pay_rate_multiplier') }}
            </span>
          </div>
        </ValidationProvider>

        <!--
        @TODO: Currently not allowing specific bill rate multipliers (it is always 1)
        <div
          v-if="!showBillRateField"
          class="bill-rate-container clickable text-grey mt-20"
          @click="onShowBillRateField"
        >
          + Add bill rate
        </div>

        <div
          v-else
          id="bill_rate_multiplier"
          class="form-group mb-10"
          :class="{ 'has-error': fieldError('bill_rate_multiplier') }"
        >
          <label
            class="control-label"
            for="bill_rate_multiplier"
          >
            Weekly overtime bill rate multiplier
          </label>
          <div class="multiplier">
            <AppInput
              v-model="form.bill_rate_multiplier"
              class="form-control"
              placeholder="1.5"
            />x member's bill rate
          </div>
          <span
            v-show="fieldError('bill_rate_multiplier')"
            class="help-block"
          >
            {{ fieldError('bill_rate_multiplier') }}
          </span>
        </div>
        -->

        <div class="modal-subheading">
          Notifications
          <AppTooltipIcon
            class="ml-5"
            text-type="lighter"
            type="info-filled"
            message="Individuals who do not wish to receive these can unsubscribe at the bottom of the email"
            placement="top"
            :size="14"
          />
        </div>

        <div class="notifications">
          <strong class="text-uppercase">1 hour before overtime threshold</strong>
          <div>Members, managers, and owners will receive an email notification</div>
        </div>

        <div class="notifications">
          <strong class="text-uppercase">When member begins overtime</strong>
          <div>Members, managers, and owners will receive an email notification</div>
        </div>

        <div class="modal-subheading">Policy members</div>
        <ValidationObserver
          ref="membersSelect"
          vid="memberObs"
          slim="slim"
        >
          <OvertimePoliciesMembersSelect
            :all-active-policies="allActivePolicies"
            :form="form"
            :members="members"
            :overtime-policy="overtimePolicy"
            :overtime-policies="overtimePolicies"
            :new-member-warning="editing"
            :disabled="disabled"
            @open-pay-dialog="openPayDialogMember"
          />
        </ValidationObserver>
      </form>

      <template #footer>
        <div
          v-if="fieldError('overtime_policy')"
          class="has-error m-0"
        >
          <div class="help-block d-flex align-items-center justify-content-end">
            <i class="hi hi-info-filled mr-5" />
            Overtime policy {{ fieldError('overtime_policy') }}
          </div>
        </div>

        <AppButton
          type="default"
          @click="closeDialog"
        >
          Cancel
        </AppButton>

        <button
          class="btn btn-primary"
          type="submit"
          form="overtime-policy-form"
          :disabled="!valid || form.processing"
        >
          Save
        </button>
      </template>
    </AppDialog>
  </ValidationObserver>
</template>
<script>
import { mapInertiaProps } from '~/utils/inertia';
import { applyTemplate } from '~/utils/url_template';
import OvertimePoliciesMembersSelect from './OvertimePoliciesMembersSelect.vue';
import { addOvertimePoliciesValidationRules } from '~/utils/overtime_policies';
import reduce from 'lodash-es/reduce';

addOvertimePoliciesValidationRules();

export default {
  name: 'OvertimePoliciesDialog',
  components: {
    OvertimePoliciesMembersSelect,
  },
  props: {
    allActivePolicies: {
      type: Array,
      required: true,
    },
    confirmedCreate: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    members: {
      type: Array,
      required: true,
    },
    overtimePolicy: {
      type: Object,
      default: () => ({}),
    },
    overtimePolicies: {
      type: Array,
      default: () => [],
    },
    overtimePoliciesUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      alreadyInPolicy: [],
      memberOfPolicy: [],
      membersWithoutPayRates: [],
      membersWithoutBillRates: [],
      membersWithoutPayAndBill: [],
      showBillRateField: false,
      form: this.$inertia.form({
        name: this.editing ? this.overtimePolicy.name : null,
        auto_add_new_users: this.editing ? this.overtimePolicy.auto_add_new_users : false,
        threshold: this.editing ? this.overtimePolicy.threshold / 3600 : null,
        pay_rate_multiplier: this.editing ? this.overtimePolicy.pay_rate_multiplier : null,
        bill_rate_multiplier: this.editing ? this.overtimePolicy.bill_rate_multiplier : null,
        user_organization_ids: this.editing ? this.overtimePolicy.user_organization_ids : [],
        errors: this.errors,
      }),
    };
  },
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
    }),
    membersOptions() {
      return this.members.map(({ id, name, pay_rate, bill_rate }) => ({ id, text: name, pay_rate, bill_rate }));
    },
    select2Options() {
      return {
        allowClear: true,
        multiple: true,
        placeholder: 'Select members',
        width: '100%',
      };
    },
    title() {
      if (this.disabled) {
        return 'View overtime policy';
      }

      return `${this.editing ? 'Edit' : 'Create weekly'} overtime policy`;
    },
    disabled() {
      return this.overtimePolicy.id && !this.overtimePolicy.can_edit;
    },
  },
  watch: {
    confirmedCreate(newVal) {
      if (newVal) {
        this.onFormSubmit();
      }
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    fieldError(field) {
      if (this.form.errors && this.form.errors[field]) {
        return this.form.errors[field][0];
      }
    },
    onFormSubmit() {
      const { origin } = window.location;
      const overtimePoliciesUrl = this.editing ? this.overtimePoliciesUrl : '/overtime_policies';
      const url = new URL(
        applyTemplate(`${overtimePoliciesUrl}`, {
          organizationId: this.organization.id,
          overtimePolicyId: this.editing ? this.overtimePolicy.id : null,
        }),
        origin,
      );
      const overtimePoliciesUrlString = url.toString();

      this.form.transform((data) => {
        const payload = {
          name: data.name,
          auto_add_new_users: data.auto_add_new_users,
          threshold: data.threshold * 3600,
          pay_rate_multiplier: data.pay_rate_multiplier,
          bill_rate_multiplier: data.bill_rate_multiplier,
          user_organization_ids: data.user_organization_ids,
        };
        const changedPayload = reduce(
          payload,
          (acc, value, key) => {
            if (this.overtimePolicy[key] !== value) {
              acc[key] = value;
            }

            return acc;
          },
          {},
        );

        return { overtime_policy: changedPayload, organization_id: this.organization.id };
      });

      const options = { onSuccess: () => this.closeDialog() };

      if (this.editing) {
        return this.form.put(overtimePoliciesUrlString, options);
      }

      if (this.confirmedCreate) {
        return this.form.post(overtimePoliciesUrlString, options);
      }

      this.$emit('open-confirm-create-dialog');
    },
    onShowBillRateField() {
      this.showBillRateField = true;
    },
    openPayDialogMember(member) {
      this.$emit('open-pay-dialog', member);
    },
  },
};
</script>
